import { Injectable, inject } from '@angular/core';
import { Store } from '@ngrx/store';
import { Observable } from 'rxjs';
import { filter, take, map } from 'rxjs/operators';
import { UserActions } from './user.actions';
import { 
  selectUserProfile, 
  selectUserLoading, 
  selectUserDeletingAccount,
  selectUserDeletingPersonalData,
  selectUserError,
  selectIsProfileLoaded,
  selectUserEmail,
  selectUserId,
  selectIsCacheValid
} from './user.selectors';
import { UserResponseSchema, UserProfileUpdateInput, UserRole } from '@app/core/api/user';

/**
 * Facade service for user state management
 * Provides clean API for components to interact with user NgRx store
 */
@Injectable({
  providedIn: 'root'
})
export class UserFacade {
  private readonly store = inject(Store);

  // Observables
  readonly profile$ = this.store.select(selectUserProfile);
  readonly loading$ = this.store.select(selectUserLoading);
  readonly deletingAccount$ = this.store.select(selectUserDeletingAccount);
  readonly deletingPersonalData$ = this.store.select(selectUserDeletingPersonalData);
  readonly error$ = this.store.select(selectUserError);
  readonly isProfileLoaded$ = this.store.select(selectIsProfileLoaded);
  readonly userEmail$ = this.store.select(selectUserEmail);
  readonly userId$ = this.store.select(selectUserId);
  readonly isCacheValid$ = this.store.select(selectIsCacheValid);

  /** User's role from profile (defaults to 'user' if not set) */
  readonly userRole$: Observable<UserRole> = this.profile$.pipe(
    map(profile => profile?.role ?? 'user')
  );

  /** Whether user has manager or admin role */
  readonly isManagerOrAdmin$: Observable<boolean> = this.userRole$.pipe(
    map(role => role === 'manager' || role === 'admin')
  );

  // Actions
  loadProfile(): void {
    this.store.dispatch(UserActions.loadProfile());
  }

  createOrSync(): void {
    this.store.dispatch(UserActions.createOrSync());
  }

  updateProfile(data: UserProfileUpdateInput): void {
    this.store.dispatch(UserActions.updateProfile({ data }));
  }

  deletePersonalData(): void {
    this.store.dispatch(UserActions.deletePersonalData());
  }

  deleteAccount(): void {
    this.store.dispatch(UserActions.deleteAccount());
  }

  setProfile(profile: UserResponseSchema): void {
    this.store.dispatch(UserActions.setProfile({ profile }));
  }

  clearProfile(): void {
    this.store.dispatch(UserActions.clearProfile());
  }

  invalidateCache(): void {
    this.store.dispatch(UserActions.invalidateCache());
  }

  // Helper methods
  waitForProfile(): Observable<UserResponseSchema> {
    return this.profile$.pipe(
      filter(profile => profile !== null),
      take(1)
    ) as Observable<UserResponseSchema>;
  }

  getProfileSnapshot(): UserResponseSchema | null {
    let profile: UserResponseSchema | null = null;
    this.profile$.pipe(take(1)).subscribe(p => profile = p);
    return profile;
  }
}
